import { useTranslation } from 'react-i18next';

import Alert from '@/components/atoms/Alert';
import { Button } from '@/components/atoms/Buttons';
import { Spinner } from '@/components/atoms/Spinner';
import ContentBlock from '@/components/molecules/ContentBlock';
import { DataTable, type DataTableColumnDef } from '@/components/organisms/DataTable';
import { usePermissions } from '@/contexts/auth';
import { type ClientIntegrationRow, useClientIntegrations } from '@/domain/providerConfig';
import { usePostHogFeatureFlag } from '@/hooks/usePostHogFeatureFlag';
import { dataSourcesPath } from '@/routes/shared/ProviderConfig/dataSourcesPath';
import { formatDateTimeWorded } from '@/utils/date';
import { parseLocale } from '@/utils/localeFormatters';

import styles from './Integrations.module.scss';

export interface IntegrationsSectionProps {
    clientId: number;
}

/** Every provider row on the client's pens, each with the way to its site's page. */
function PenRows({ clientId }: IntegrationsSectionProps) {
    const { t, i18n } = useTranslation();
    const { rows, isLoading, error } = useClientIntegrations(clientId);

    if (error !== null)
        return (
            <Alert tone="danger">
                {t(
                    error.kind === 'forbidden'
                        ? 'providerConfig.errors.forbidden'
                        : 'providerConfig.integrations.loadFailed'
                )}
            </Alert>
        );
    if (isLoading) return <Spinner size="lg" />;
    if (rows.length === 0)
        return <p className={styles.note}>{t('providerConfig.integrations.empty')}</p>;

    const locale = parseLocale(i18n.language);
    const columns: DataTableColumnDef<ClientIntegrationRow>[] = [
        {
            id: 'site',
            header: t('providerConfig.integrations.site'),
            cell: ({ row }) => row.original.locationName,
        },
        {
            id: 'pen',
            header: t('providerConfig.integrations.pen'),
            cell: ({ row }) => row.original.cageName,
        },
        {
            id: 'provider',
            header: t('providerConfig.integrations.provider'),
            cell: ({ row }) =>
                t(`providerConfig.providers.${row.original.providerName}`, {
                    defaultValue: row.original.providerName,
                }),
        },
        {
            id: 'status',
            header: t('providerConfig.integrations.status'),
            cell: ({ row }) =>
                t(
                    row.original.isActive
                        ? 'providerConfig.integrations.active'
                        : 'providerConfig.integrations.inactive'
                ),
        },
        {
            id: 'lastFetched',
            header: t('providerConfig.integrations.lastFetched'),
            cell: ({ row }) =>
                row.original.lastFetchedAt
                    ? formatDateTimeWorded(row.original.lastFetchedAt, 'minute', locale)
                    : t('providerConfig.integrations.neverFetched'),
        },
        {
            id: 'dataSources',
            header: t('providerConfig.integrations.dataSources'),
            cell: ({ row }) => (
                <Button
                    to={dataSourcesPath(clientId, row.original.locationId)}
                    variant="table"
                    size="small">
                    {t('providerConfig.integrations.openSite', {
                        site: row.original.locationName,
                    })}
                </Button>
            ),
        },
    ];
    return (
        <DataTable
            data={rows}
            columns={columns}
            getRowId={(row) => String(row.rowId)}
            responsive
            density="compact"
            enableSorting={false}
        />
    );
}

/**
 * The client's provider connections, for OptoScale admins while the provider-config-ui
 * flag is on. Off, it renders nothing and sends no request, as before it existed. The
 * connections that belong to no client are on the internal Data shares page.
 */
function IntegrationsSection({ clientId }: IntegrationsSectionProps) {
    const { t } = useTranslation();
    const { isOptoScaleAdmin } = usePermissions();
    const { enabled } = usePostHogFeatureFlag('provider-config-ui');
    if (!isOptoScaleAdmin || !enabled) return null;

    return (
        <ContentBlock title={t('providerConfig.integrations.title')}>
            <PenRows clientId={clientId} />
        </ContentBlock>
    );
}

export default IntegrationsSection;
export { IntegrationsSection };
